// Copyright Epic Games, Inc. All Rights Reserved.

import { IAppState } from "../index";
import {
  IErrorCodeLocalizationState,
  localizationFeatureStoreKey,
  LocalizationMessageIds,
} from "./reducer";

const wildCardErrorCodesToRegExpMap: Map<string, RegExp> = new Map<
  string,
  RegExp
>();

/**
 * @desc Selects error code localization information from the state.
 * We support error codes that ends with wild card e.g. ERR-*. Will build a regexp
 * and cache it for wildcard error code. If no error code was found will return
 * fallback error code localization info.
 * @param state
 * @param errorCode
 */
export const selectLocErrorCodeInfo = (
  state: IAppState,
  errorCode: string
): IErrorCodeLocalizationState => {
  const errorCodesLocalization = state[localizationFeatureStoreKey].errorCodes;
  const potentialLocInfo = errorCodesLocalization[errorCode];

  if (potentialLocInfo) {
    return potentialLocInfo;
  }

  // Check wildcard error codes.
  if (!potentialLocInfo) {
    const wildCardErrorCodes = Object.keys(
      errorCodesLocalization
    ).filter((errorCode) => errorCode.endsWith("*"));

    for (const wildCardErrorCode of wildCardErrorCodes) {
      if (wildCardErrorCodesToRegExpMap.has(wildCardErrorCode)) {
        // We need to test the RegExp here, otherwise we could rebuild the RegExp even
        // if we have the RegExp but it has not matched.
        if (
          wildCardErrorCodesToRegExpMap.get(wildCardErrorCode)?.test(errorCode)
        ) {
          return errorCodesLocalization[wildCardErrorCode];
        }
      } else {
        // We support only wildcard for end of the error code.
        const nonWildCardPart = wildCardErrorCode.slice(
          0,
          wildCardErrorCode.length - 1
        );
        const regExp = new RegExp(`^${nonWildCardPart}.+$`);
        wildCardErrorCodesToRegExpMap.set(wildCardErrorCode, regExp);

        if (regExp.test(errorCode)) {
          return errorCodesLocalization[wildCardErrorCode];
        }
      }
    }
  }

  return errorCodesLocalization.fallback;
};

export const selectLocMessage = (
  messageId: LocalizationMessageIds,
  defaultMessage: string
) => (state: IAppState): string => {
  return (
    state[localizationFeatureStoreKey].messages[messageId] || defaultMessage
  );
};

export const selectLang = (state: IAppState): string => {
  return state[localizationFeatureStoreKey].lang;
};

export const selectLocErrorCodeTitle = (errorCode: string) => (
  state: IAppState
): string => {
  return selectLocErrorCodeInfo(state, errorCode).title;
};

export const selectLocErrorCodeDescription = (errorCode: string) => (
  state: IAppState
): string => {
  return selectLocErrorCodeInfo(state, errorCode).description;
};
